import { baseApiService } from '../baseApiService';
import {
    PatientSearchResponse,
    PatientSearchRequest,
    PatientDetailsResponse,
    PatientScriptSureCookieResponse,
    PatientScriptSureCookieRequest,
    PatientDischargeRequest,
    PatientDischargeResponse,
    PatientConsultationResponse,
    PatientConsultationRequest,
    DropdownOptionsResponse,
    SearchDrugListResponse,
    SearchDrugFormatListResponse,
    QueueMedOrderResponse,
    QueueMedOrderRequest,
    OrderResponse,
    OrderUpdates,
    UpdateMedOrderRequest,
    OrderStatusEnum,
    MedOrderResponse,
    OrderSourceEnum,
    OrderTemplate,
    CreateOrderFromMedSheet,
    StructuredActionsResponse,
    StructuredFormResponse,
    StructuredRouteResponse,
    StructuredFrequenciesResponse,
    DrugInteractionResponse,
    DrugInteractionRequest,
    CreateOrdersFromOrderTemplates,
} from '../../models/PatientModels';

export const patientAPI = baseApiService
    .enhanceEndpoints({
        addTagTypes: ['orders', 'orderTemplates', 'medications'],
    })
    .injectEndpoints({
        endpoints: (builder) => ({
            patientSearchResult: builder.mutation<
                PatientSearchResponse,
                PatientSearchRequest
            >({
                query: (req) => {
                    return {
                        url: '/v1/patient/search',
                        method: 'POST',
                        body: req,
                    };
                },
            }),
            getPatientDetails: builder.query<
                PatientDetailsResponse,
                { id: string }
            >({
                query: ({ id }) => ({
                    url: `/v1/patient/${id}`,
                    method: 'GET',
                }),
            }),
            patientDetails: builder.mutation<PatientDetailsResponse, any>({
                query: (_id) => ({
                    url: `/v1/patient/${_id}`,
                    method: 'GET',
                }),
            }),
            postPatientRecord: builder.mutation<
                PatientDetailsResponse,
                PatientDetailsResponse
            >({
                query: (req) => {
                    return {
                        url: '/v1/patient',
                        method: 'POST',
                        body: req,
                    };
                },
            }),
            dischargePatient: builder.mutation<
                PatientDischargeResponse,
                { id: string; req: PatientDischargeRequest }
            >({
                query: ({ id, req }) => {
                    return {
                        url: `/v1/patient/${id}/discharge`,
                        method: 'PUT',
                        body: req,
                    };
                },
            }),
            requestConsultation: builder.mutation<
                PatientConsultationResponse,
                { req: PatientConsultationRequest }
            >({
                query: ({ req }) => {
                    return {
                        url: `/v1/patient/consult`,
                        method: 'POST',
                        body: req,
                    };
                },
            }),
            getDropDownOptions: builder.mutation<any, any>({
                query: () => {
                    return {
                        url: `/v1/patient/dropdown-options`,
                        method: 'GET',
                    };
                },
            }),
            dropdownOptions: builder.query<DropdownOptionsResponse, void>({
                query: () => {
                    return {
                        url: `/v1/patient/dropdown-options`,
                        method: 'GET',
                    };
                },
            }),
            getPractiontionerOptions: builder.mutation<any, any>({
                query: (req) => {
                    return {
                        url: `/v1/team/${req}/prescribers`,
                        method: 'GET',
                    };
                },
            }),
            fetchTeamOptions: builder.mutation<any, any>({
                query: (req) => {
                    return {
                        url: `/v1/Team`,
                        method: 'GET',
                    };
                },
            }),
            createMedOrder: builder.mutation<
                QueueMedOrderResponse,
                QueueMedOrderRequest
            >({
                query: ({ data, patientId }) => ({
                    url: `v1/patient/${patientId}/orders/draft`,
                    method: 'POST',
                    body: data,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }),
                invalidatesTags: (result, error, arg) => [
                    { type: 'orders', patientId: arg.patientId },
                ],
            }),
            updateMedOrder: builder.mutation<
                QueueMedOrderResponse,
                UpdateMedOrderRequest
            >({
                query: ({ data, orderId }) => ({
                    url: `v1/patient/orders/${orderId}/draft`,
                    method: 'PUT',
                    body: data,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }),
                invalidatesTags: (result, error, arg) => [
                    { type: 'orders', patientId: arg.patientId },
                ],
            }),
            fetchTeamOptionsTableData: builder.mutation<any, any>({
                query: (req) => {
                    return {
                        url: `/v1/team/patients?teamId=${req}`,
                        method: 'GET',
                    };
                },
            }),
            postPatientScriptSureCookie: builder.mutation<
                PatientScriptSureCookieResponse,
                PatientScriptSureCookieRequest
            >({
                query: (req) => {
                    return {
                        url: '/personal/script-sure-cookie',
                        method: 'POST',
                        body: req,
                    };
                },
            }),
            postPatientAllergyBypassUpload: builder.mutation<any, any>({
                query: (req) => {
                    return {
                        url: '/v1/bypass/upload',
                        method: 'POST',
                        body: req,
                    };
                },
            }),
            postPatientGetHelp: builder.mutation<any, any>({
                query: (req) => {
                    return {
                        url: '/v1/logging/scriptsureiframe',
                        method: 'POST',
                        body: req,
                    };
                },
            }),

            searchDrugList: builder.mutation<
                SearchDrugListResponse[],
                { value: string }
            >({
                query: ({ value }) => ({
                    url: 'v1/drugs/scriptsure/search',
                    method: 'POST',
                    body: JSON.stringify(value),
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }),
            }),
            searchDrugFormatList: builder.mutation<
                SearchDrugFormatListResponse[],
                { value: string }
            >({
                query: ({ value }) => ({
                    url: 'v1/drugs/scriptsure/drugformat/search',
                    method: 'POST',
                    body: JSON.stringify(value),
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }),
            }),
            queueMedOrder: builder.mutation<
                QueueMedOrderResponse,
                QueueMedOrderRequest
            >({
                query: ({ data, patientId }) => ({
                    url: `v1/patient/${patientId}/orders`,
                    method: 'POST',
                    body: data,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }),
                invalidatesTags: (result, error, arg) => [
                    { type: 'orders', patientId: arg.patientId },
                ],
            }),
            getPatientOrders: builder.query<
                OrderResponse[],
                { patientId: string; orderStatus: number }
            >({
                query: ({ patientId, orderStatus }) => ({
                    url: `v1/patient/${patientId}/orders?orderStatus=${orderStatus}`,
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }),
                providesTags: (result, error, arg) => [
                    { type: 'orders', patientId: arg.patientId },
                ],
            }),
            // Update All patient Orders to Pending
            updateAllPatientOrders: builder.mutation<
                OrderUpdates,
                { value: { orderIds: string[] }; patientId: string }
            >({
                query: ({ value }) => ({
                    url: `v1/patient/orders`,
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(value),
                }),
                invalidatesTags: (result, error, arg) => [
                    { type: 'orders', patientId: arg.patientId },
                ],
            }),
            updatePatientOrder: builder.mutation<
                OrderUpdates,
                { orderId: string; patientId: string }
            >({
                query: ({ orderId }) => ({
                    url: `v1/patient/orders/${orderId}`,
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }),
                invalidatesTags: (result, error, arg) => [
                    { type: 'orders', patientId: arg.patientId },
                ],
            }),
            updateOrderStatus: builder.mutation<
                OrderUpdates,
                {
                    orderId: string;
                    orderStatus: OrderStatusEnum;
                    patientId: string;
                }
            >({
                query: ({ orderId, orderStatus }) => ({
                    url: `v1/patient/orders/status?orderId=${orderId}&orderStatus=${orderStatus}`,
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }),
                invalidatesTags: (result, error, arg) => [
                    { type: 'orders', patientId: arg.patientId },
                ],
            }),
            getPatientMedications: builder.query<
                MedOrderResponse[],
                { patientId: string }
            >({
                query: ({ patientId }) => ({
                    url: `v1/patient/${patientId}/medications`,
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }),
                providesTags: (result, error, arg) => [
                    { type: 'medications', patientId: arg.patientId },
                ],
            }),
            getOrderTemplates: builder.query<
                OrderTemplate[],
                { source: OrderSourceEnum; patientId: string }
            >({
                query: ({ source, patientId }) => ({
                    url: `v1/patient/${patientId}/orders/templates?source=${source}`,
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }),
                providesTags: (result, error, arg) => [
                    { type: 'orderTemplates', patientId: arg.patientId },
                ],
            }),
            createOrderFromMedSheet: builder.mutation<
                OrderResponse,
                CreateOrderFromMedSheet
            >({
                query: ({ medSheetId }) => ({
                    method: 'POST',
                    url: `v1/patient/orders/template/medsheets/${medSheetId}/transform`,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }),
                invalidatesTags: (result, error, arg) => [
                    { type: 'orders', patientId: arg.patientId },
                ],
            }),

            createOrderFromOrderTemplate: builder.mutation<any, CreateOrdersFromOrderTemplates>({
                query: (data: CreateOrdersFromOrderTemplates) => ({
                    method: 'POST',
                    url: `v1/patient/${data.patientId}/orders/templates/transform`,
                    body: {orderTemplateIds: data.orderTemplateIds},
                }),
            }),


            deleteOrderTemplate: builder.mutation<boolean, string>({
                query: (orderTemplateId: string) => ({
                    method: 'DELETE',
                    url: `v1/patient/orders/template/${orderTemplateId}`,
                })
            }),

            getStructuredActions: builder.query<
                StructuredActionsResponse[],
                void
            >({
                query: () => ({
                    url: 'v1/drugs/scriptsure/actions',
                    method: 'GET',
                }),
            }),
            getStructuredForms: builder.query<StructuredFormResponse[], void>({
                query: () => ({
                    url: 'v1/drugs/scriptsure/forms',
                    method: 'GET',
                }),
            }),
            getStructuredRoute: builder.query<StructuredRouteResponse[], void>({
                query: () => ({
                    url: 'v1/drugs/scriptsure/routes',
                    method: 'GET',
                }),
            }),
            getStructuredFrequencies: builder.query<
                StructuredFrequenciesResponse[],
                void
            >({
                query: () => ({
                    url: 'v1/drugs/scriptsure/frequencies',
                    method: 'GET',
                }),
            }),
            getDrugInteraction: builder.mutation<
                DrugInteractionResponse,
                { req: DrugInteractionRequest }
            >({
                query: ({ req }) => ({
                    url: `v1/patient/orders/drugtodrug-interaction`,
                    method: 'POST',
                    body: JSON.stringify(req),
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }),
            }),

            getLatestEMRUpdate: builder.query<string, { patientId: string }>({
                query: ({ patientId }) => ({
                    url: `v1/rpa/${patientId}/latest?type=1`,
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
            }),
        }),
    });

export const {
    usePatientSearchResultMutation,
    useGetPatientDetailsQuery,
    usePatientDetailsMutation,
    usePostPatientRecordMutation,
    useDischargePatientMutation,
    useRequestConsultationMutation,
    useGetDropDownOptionsMutation,
    useDropdownOptionsQuery,
    useGetPractiontionerOptionsMutation,
    useFetchTeamOptionsMutation,
    useFetchTeamOptionsTableDataMutation,
    usePostPatientScriptSureCookieMutation,
    usePostPatientAllergyBypassUploadMutation,
    usePostPatientGetHelpMutation,
    useSearchDrugListMutation,
    useSearchDrugFormatListMutation,
    useCreateMedOrderMutation,
    useGetPatientOrdersQuery,
    useUpdateAllPatientOrdersMutation,
    useUpdatePatientOrderMutation,
    useUpdateMedOrderMutation,
    useUpdateOrderStatusMutation,
    useGetPatientMedicationsQuery,
    useGetOrderTemplatesQuery,
    useCreateOrderFromMedSheetMutation,
    useGetStructuredActionsQuery,
    useGetStructuredFormsQuery,
    useGetStructuredRouteQuery,
    useGetStructuredFrequenciesQuery,
    useGetDrugInteractionMutation,
    useGetLatestEMRUpdateQuery,
    useDeleteOrderTemplateMutation,
    useCreateOrderFromOrderTemplateMutation
} = patientAPI;
